import React from "react";
import { motion } from "framer-motion";
import { TopProduct } from "../types/dashboard";
import { useUserTheme } from "src/shared/utils/useUserTheme";

interface TopProductsProps {
  products: TopProduct[];
}

const RANK_MEDALS = ["🥇", "🥈", "🥉"];

const TopProducts: React.FC<TopProductsProps> = ({ products }) => {
  const { bgClass } = useUserTheme();
  if (!products || products.length === 0) {
    return (
      <div className="rounded-2xl bg-white/70 backdrop-blur-xl border border-gray-200 shadow-sm p-6">
        <p className="text-sm text-gray-400 text-center">No product insights available</p>
      </div>
    );
  }

  const maxQuantity = Math.max(...products.map((p) => p.totalQuantity));

  return (
    <div className="relative rounded-2xl bg-white/70 backdrop-blur-xl border border-gray-200 shadow-[0_8px_30px_rgba(0,0,0,0.06)] overflow-hidden">
      {/* Subtle Gradient Glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-white/10 pointer-events-none" />

      {/* Header */}
      <div className="relative px-6 pt-6 pb-4 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-gray-900">Top Products</h2>
          <p className="text-xs text-gray-400 mt-1">Performance ranked by quantity sold</p>
        </div>

        <div className="text-xs font-medium text-gray-600 bg-gray-100/70 backdrop-blur px-3 py-1 rounded-full border border-gray-200">{products.length} items</div>
      </div>

      {/* Divider */}
      <div className="border-t border-gray-100/80" />

      {/* Podium */}
      <div className="px-6 pt-6 pb-5">
        <div className="flex items-end justify-center gap-6 min-h-[160px]">
          {[1, 0, 2].map((idx) => {
            const product = products[idx];
            if (!product) return <div key={idx} className="flex-1 max-w-[110px]" />;

            const height = maxQuantity > 0 ? Math.max(40, (product.totalQuantity / maxQuantity) * 120) : 40;

            return (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} className="flex flex-col items-center gap-2 flex-1 max-w-[110px]">
                {/* Medal */}
                <span className="text-xl">{RANK_MEDALS[idx]}</span>

                {/* Name */}
                <p className="text-xs font-medium text-gray-700 text-center line-clamp-2 h-8">{product.productName || "Unknown"}</p>

                {/* Value */}
                <div className="text-center">
                  <p className="text-sm font-semibold text-gray-900">
                    {product.totalQuantity.toLocaleString()} <span className="text-[10px] font-normal text-gray-400">qty</span>
                  </p>
                  <p className="text-[10px] text-gray-400">{product.totalSales.toLocaleString()} sales</p>
                </div>

                {/* Bar */}
                <motion.div initial={{ height: 0 }} animate={{ height }} transition={{ duration: 0.6, ease: "easeOut" }} className={`w-full rounded-xl ${bgClass} shadow-inner`} />
              </motion.div>
            );
          })}
        </div>

        {/* List */}
        <div className="mt-6 space-y-2">
          {products.slice(3).map((product, i) => {
            const pct = maxQuantity > 0 ? Math.round((product.totalQuantity / maxQuantity) * 100) : 0;

            return (
              <motion.div
                key={product.productName || i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ scale: 1.01 }}
                className="group flex items-center gap-4 px-4 py-3 rounded-xl bg-white/60 backdrop-blur border border-gray-200 hover:shadow-sm transition-all"
              >
                {/* Rank */}
                <span className="text-xs font-semibold text-gray-400 w-6 text-center">#{i + 4}</span>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{product.productName || "Unknown"}</p>

                  <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.6 }}
                      className={`h-full rounded-full ${bgClass} shadow-inner`}
                    />
                  </div>
                </div>

                {/* Stats */}
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">
                    {product.totalQuantity.toLocaleString()} <span className="text-[10px] font-normal text-gray-400">qty</span>
                  </p>
                  <p className="text-xs text-gray-400">{product.totalSales.toLocaleString()} sales</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TopProducts;